import { User } from "../../../../../api/accessControl/User.ts";
import Shell from "../../../../../components/specific/Shell.tsx";
import { useEffect, useMemo, useState, useRef } from "react";
import { BackendApi } from "../../../../../api/api.ts";
import { fetchDocumentContentBlob } from "../../../../../api/fetchDocumentContent.ts";
import { useNotifications } from "../../../../../lib/NotificationContext.tsx";
import { Link } from "react-router-dom";
import { SharedDocument } from "./SharedDocument.ts";
import { SharedDocumentsTable } from "./SharedDocumentsTable.tsx";
import { MissingDocumentsTable } from "./MissingDocumentsTable.tsx";
import { DocumentDropzone } from "./DocumentDropzone";
import { PendingDraftMatchesSection } from "./PendingDraftMatchesSection.tsx";
import { useDraftMatches } from "./hooks/useDraftMatches.ts";
import CopyToClipboard from "../../../../../components/general/CopyToClipboard";
import { InboxArrowDownIcon } from "@heroicons/react/24/outline/index";
import WideDrawer from "../../../../../components/general/WideDrawer.tsx";
import prettyBytes from "pretty-bytes";
import { DocumentPreview } from "../../../../../views/DocumentDrawer/components/DocumentPreview.tsx";
import type { DocumentDirection } from "../../../../../generated/enums.ts";
import { UploadQueueList, useUploadQueueContext } from "../../../../../views/UploadQueue";

const maxSize = 1024 * 1024 * 500
const uploadQueueMaxFiles = 1000
const uploadQueueMaxSize = 1024 * 1024 * 15
const uploadQueueFileTypesLabel = "PDF, foto's, Office en XML"

const supportedFileTypes = {
    'image/jpeg': ['.jpeg', '.jpg'],
    'image/png': ['.png'],
    'image/heic': ['.heic'],
    'image/heif': ['.heif'],
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/msword': ['.doc'],
    'application/vnd.oasis.opendocument.text': ['.odt'],
    'application/xml': ['.xml'],
}


const supportedFileExtensions = Object.values(supportedFileTypes).flat()


export default function ShareDocumentsPage({ currentUser }: { currentUser: User }) {
    const [isDocumentDrawerOpen, setIsDocumentDrawerOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<SharedDocument | null>(null);

    const [fileUploadHistory, setFileUploadHistory] = useState<SharedDocument[]>([])
    const [fadingIds, setFadingIds] = useState<Set<string>>(new Set())
    const [attachingIds, setAttachingIds] = useState<Set<string>>(new Set())
    const [documentCollectionEntrepreneurForwardAddress, setDocumentCollectionEntrepreneurForwardAddress] = useState<string>('')

    const { dispatch } = useNotifications();

    const backendApi = new BackendApi(currentUser.getToken());

    const canDraftMatchDocuments = currentUser.flags.getFlag('ENTREPRENEUR__CAN_DRAFT_MATCH_DOCUMENTS')

    const uploadQueue = useUploadQueueContext();
    if (!uploadQueue) throw new Error('ShareDocumentsPage must be rendered inside an UploadQueueProvider');
    const { settledRunCount } = uploadQueue;

    const seenRunCountRef = useRef(settledRunCount);
    useEffect(() => {
        if (settledRunCount === seenRunCountRef.current) return;
        seenRunCountRef.current = settledRunCount;
        void onLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settledRunCount]);

    const emailAddressChip = currentUser.flags.getFlag('CAN_SEE_DOCUMENT_COLLECTION_EMAIL') && documentCollectionEntrepreneurForwardAddress && (
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm shadow-sm">
            <InboxArrowDownIcon className="size-5 shrink-0 text-gray-400" />
            <span className="shrink-0 text-gray-500">Mail naar</span>
            <CopyToClipboard text={documentCollectionEntrepreneurForwardAddress} className="min-w-0 break-all" />
        </div>
    );

    const enqueueUpload = (direction: DocumentDirection) => (files: File[]) => {
        if (files.length > 0) uploadQueue.enqueue(files, direction);
    };

    const {
        pendingDrafts,
        transitioningDrafts,
        missingDocuments,
        setMissingDocuments,
        derivedMissingDocuments,
        isRefreshing,
        loadDraftMatches,
        fetchDraftMatchData,
        refreshDraftMatches,
        cancelDraftMatch,
        cleanup,
    } = useDraftMatches({ currentUser, canDraftMatchDocuments })


    const uploadedDocumentIds = useMemo(
        () => new Set(fileUploadHistory.map((document) => document.id)),
        [fileUploadHistory],
    );

    const handleOpenRejectedDocument = (documentId: string) => {
        const document = fileUploadHistory.find((uploadedDocument) => uploadedDocument.id === documentId);
        if (document) handleDocumentClick(document);
    };

    const handleDocumentClick = async (document: SharedDocument) => {
        setSelectedDocument(document);
        setIsDocumentDrawerOpen(true);

        try {
            const blob = await fetchDocumentContentBlob(backendApi, 'entrepreneur/get-document-content', {
                document_id: document.id,
            });
            const documentWithFileData = { ...document, file_blob: blob };
            setSelectedDocument(documentWithFileData);
        } catch (error) {
            console.error('Error fetching document binary data:', error);
            dispatch({
                title: 'Fout opgetreden',
                description: 'Het document kon niet worden geladen. Probeer het opnieuw.',
                type: 'error',
            });
        }
    };





    const canSeeUnmatchedTransactions = currentUser.flags.getFlag('ENTREPRENEUR__CAN_SEE_UNMATCHED_TRANSACTIONS')


    async function onLoad() {
        const result = await backendApi.Entrepreneur__uploadedDocuments(null)
        setFileUploadHistory(result as unknown as SharedDocument[])

        await loadDraftMatches()

        let documentCollectionEntrepreneurForwardAddressResponse;
        try {

            documentCollectionEntrepreneurForwardAddressResponse = await backendApi.Entrepreneur__documentCollectionEmail({})
            setDocumentCollectionEntrepreneurForwardAddress(
                documentCollectionEntrepreneurForwardAddressResponse.email
            )
        } catch {
            setDocumentCollectionEntrepreneurForwardAddress("Could not retrieve address")
        }


    }

    async function handleMarkAsPrivate(transactionId: string) {
        const removedDocument = missingDocuments.find(doc => doc.transaction_id === transactionId);
        setFadingIds(prev => new Set(prev).add(transactionId));

        setTimeout(() => {
            setMissingDocuments(prev => prev.filter(doc => doc.transaction_id !== transactionId));
            setFadingIds(prev => {
                const next = new Set(prev);
                next.delete(transactionId);
                return next;
            });
        }, 400);

        try {
            await backendApi.Entrepreneur__markTransactionAsPrivate({ transaction_id: transactionId });
        } catch (error) {
            setFadingIds(prev => {
                const next = new Set(prev);
                next.delete(transactionId);
                return next;
            });
            if (removedDocument) {
                setMissingDocuments(prev => [...prev, removedDocument]);
            }
            dispatch({
                title: 'Fout opgetreden',
                description: 'De transactie kon niet als privé worden gemarkeerd. Probeer het opnieuw.',
                type: 'error',
            });
        }
    }

    async function handleAttachFileToMDR(transactionId: string, file: File) {
        if (attachingIds.has(transactionId)) {
            return
        }

        const isAcceptedType = file.type in supportedFileTypes
            || supportedFileExtensions.some(ext => file.name.toLowerCase().endsWith(ext.toLowerCase()))
        if (!isAcceptedType) {
            dispatch({
                title: 'Bestandstype niet ondersteund',
                description: `Alleen ${supportedFileExtensions.join(', ')} worden ondersteund.`,
                type: 'error',
            })
            return
        }
        if (file.size > maxSize) {
            dispatch({
                title: 'Bestand te groot',
                description: 'Het bestand is te groot om te koppelen.',
                type: 'error',
            })
            return
        }

        const transaction = missingDocuments.find(doc => doc.transaction_id === transactionId)
        const suggestedMatchDirection: DocumentDirection = transaction && transaction.total_amount_payable.signed_amount < 0
            ? 'purchase_invoice'
            : 'sales_invoice'

        setAttachingIds(prev => new Set(prev).add(transactionId))
        try {
            const formData = new FormData()
            formData.append('files', file, file.name)
            formData.append('shared_via', 'web')
            formData.append('suggested_match_transaction_id', transactionId)
            formData.append('suggested_match_direction', suggestedMatchDirection)

            const response = await backendApi.postFormData('document-collection/share-files', formData)
            if (!response.ok) {
                dispatch({
                    title: 'Koppelen mislukt',
                    description: 'Het document kon niet aan de transactie worden gekoppeld. Probeer het opnieuw.',
                    type: 'error',
                })
                return
            }
            dispatch({
                title: 'Document gekoppeld',
                description: 'Het document is gekoppeld en wordt nu gecontroleerd.',
                type: 'success',
            })

            setFadingIds(prev => new Set(prev).add(transactionId))
            setTimeout(() => {
                setFadingIds(prev => {
                    const next = new Set(prev)
                    next.delete(transactionId)
                    return next
                })
                fetchDraftMatchData().catch(() => {})
                backendApi.Entrepreneur__uploadedDocuments(null)
                    .then(result => setFileUploadHistory(result as unknown as SharedDocument[]))
                    .catch(() => {})
            }, 400)
        } catch (error) {
            dispatch({
                title: 'Koppelen mislukt',
                description: 'Er ging iets mis bij het koppelen van het document. Probeer het opnieuw.',
                type: 'error',
            })
            throw error
        } finally {
            setAttachingIds(prev => {
                const next = new Set(prev)
                next.delete(transactionId)
                return next
            })
        }
    }

    async function handleCancelDraftMatch(draftMatchId: string) {
        try {
            await cancelDraftMatch(draftMatchId)
            dispatch({
                title: 'Koppeling geannuleerd',
                description: 'Het document is losgekoppeld van de transactie.',
                type: 'success',
            })
        } catch (error) {
            dispatch({
                title: 'Annuleren mislukt',
                description: 'De koppeling kon niet worden geannuleerd. Mogelijk wordt deze al gecontroleerd.',
                type: 'error',
            })
        }
    }

    useEffect(() => {
        onLoad()
        return () => {
            cleanup()
        }
    }, []);


    return (
        <>
            <WideDrawer
                open={isDocumentDrawerOpen}
                title="Document Bekijken"
                updateOpenState={(open) => {
                    setIsDocumentDrawerOpen(open);
                }}
            >
                {selectedDocument && (
                    <div className="h-full flex flex-col">
                        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                            <h3 className="font-semibold text-lg mb-2" title={selectedDocument.file_name}>
                                {selectedDocument.file_name.slice(42)}
                            </h3>
                            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                                <div>
                                    <span className="font-medium">Type:</span> {selectedDocument.mime_type}
                                </div>
                                <div>
                                    <span className="font-medium">Grootte:</span> {prettyBytes(selectedDocument.size)}
                                </div>
                                <div>
                                    <span
                                        className="font-medium">Geüpload op:</span> {new Date(selectedDocument.created_at).toLocaleString('nl-NL')}
                                </div>
                                <div>
                                    <span
                                        className="font-medium">Status:</span> {selectedDocument.document_direction ? 'Verwerkt' : 'In behandeling'}
                                </div>
                            </div>
                        </div>

                        <div className="flex-1 bg-gray-100 rounded-lg p-4">
                            <DocumentPreview
                                document={
                                    selectedDocument && {
                                        file_name: selectedDocument.file_name,
                                        file_blob: selectedDocument.file_blob,
                                        mimetype: selectedDocument.mime_type,
                                    }
                                }
                            />
                        </div>
                    </div>
                )}
            </WideDrawer>

            <Shell
                currentUser={currentUser}
                title="Documenten delen"
                subtitle="Upload facturen en bonnetjes. We lezen ze automatisch uit en koppelen ze aan je transacties."
                titleActions={emailAddressChip && <div className="hidden xl:block">{emailAddressChip}</div>}
            >
                <div className="upload-container grid grid-cols-1 md:grid-cols-2 gap-5">
                    <DocumentDropzone
                        label="Inkoopfacturen en bonnetjes"
                        documentDirection="purchase_invoice"
                        supportedFileTypes={supportedFileTypes}
                        supportedFileExtensions={supportedFileExtensions}
                        maxFiles={uploadQueueMaxFiles}
                        maxSize={uploadQueueMaxSize}
                        fileTypesLabel={uploadQueueFileTypesLabel}
                        setFiles={enqueueUpload('purchase_invoice')}
                    />
                    <DocumentDropzone
                        label="Verkoopfacturen"
                        documentDirection="sales_invoice"
                        supportedFileTypes={supportedFileTypes}
                        supportedFileExtensions={supportedFileExtensions}
                        maxFiles={uploadQueueMaxFiles}
                        maxSize={uploadQueueMaxSize}
                        fileTypesLabel={uploadQueueFileTypesLabel}
                        setFiles={enqueueUpload('sales_invoice')}
                    />
                </div>

                {emailAddressChip && <div className="mt-4 flex xl:hidden">{emailAddressChip}</div>}

                <UploadQueueList
                    items={uploadQueue.items}
                    secondsLeft={uploadQueue.secondsLeft}
                    isStalled={uploadQueue.isStalled}
                    onResume={uploadQueue.resume}
                    onRetry={uploadQueue.retry}
                    onRetryAllFailed={uploadQueue.retryAllFailed}
                    onClearFinished={uploadQueue.clearFinished}
                />

                {canDraftMatchDocuments && (
                    <PendingDraftMatchesSection
                        pendingDrafts={pendingDrafts}
                        transitioningDrafts={transitioningDrafts}
                        onRefresh={refreshDraftMatches}
                        isRefreshing={isRefreshing}
                        onCancel={handleCancelDraftMatch}
                    />
                )}

                {canSeeUnmatchedTransactions && derivedMissingDocuments.length > 0 && (
                    <>
                        <h2 className="weight-600 text-2xl mb-6 mt-16 flex items-center gap-3">
                            Documenten aan te leveren
                            <span className="text-sm font-medium text-gray-500 bg-gray-100 rounded-full px-3 py-0.5">
                                {derivedMissingDocuments.length}
                            </span>
                        </h2>
                        <MissingDocumentsTable
                            data={derivedMissingDocuments}
                            onMarkAsPrivate={handleMarkAsPrivate}
                            showPrivateAction={canDraftMatchDocuments}
                            fadingIds={fadingIds}
                            onAttachFile={canDraftMatchDocuments ? handleAttachFileToMDR : undefined}
                            supportedFileTypes={canDraftMatchDocuments ? supportedFileTypes : undefined}
                            supportedFileExtensions={canDraftMatchDocuments ? supportedFileExtensions : undefined}
                            maxSize={canDraftMatchDocuments ? maxSize : undefined}
                            attachingIds={attachingIds}
                            onOpenRejectedDocument={handleOpenRejectedDocument}
                            openableDocumentIds={uploadedDocumentIds}
                        />
                    </>
                )}


                <h2 className="weight-600 text-2xl mb-6 mt-16 ">Geuploade documenten</h2>
                <SharedDocumentsTable data={fileUploadHistory} onDocumentClick={handleDocumentClick}
                    allowFullRowClick={true} />
            </Shell>
        </>
    )
}
